import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, ChevronRight, CheckCircle2, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CARD, H2 } from "@/components/client/ClientAreaParts";

const FIELD = "w-full px-4 py-3 border border-stone-300 rounded-2xl text-base text-stone-800 leading-relaxed focus:outline-none focus:border-[hsl(var(--copper))] transition-colors";
const BTN = "inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[hsl(var(--forest))] text-white text-base font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed";
const LABEL_TXT = "block text-sm font-semibold text-[hsl(var(--forest))] mb-2";

function formatWhen(iso) {
    const dt = iso ? new Date(iso) : null;
    if (!dt || isNaN(dt)) return "";
    return dt.toLocaleString("pt-PT", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function StatusPill({ status, testid }) {
    const open = status !== "resolved";
    return (
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${open ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-800"}`} data-testid={testid}>
            {open ? "Em aberto" : "Resolvida"}
        </span>
    );
}

function NewPill({ testid }) {
    return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap bg-[hsl(var(--copper))] text-white" data-testid={testid}>
            <span className="w-1.5 h-1.5 rounded-full bg-white" aria-hidden />Nova mensagem
        </span>
    );
}

export function ConversationsPanel({ token, conversations, composing, setComposing, onReload }) {
    const [openId, setOpenId] = useState(null);
    const current = conversations.find((c) => c.id === openId);
    if (current) {
        return <ConversationThread token={token} conv={current} onBack={() => setOpenId(null)} onReload={onReload} />;
    }
    const created = async (id) => { setComposing(false); await onReload(); setOpenId(id); };
    return (
        <section className={CARD} data-testid="client-conversations-section">
            <div className="max-w-3xl">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div>
                        <h2 className={H2}>Conversas</h2>
                        <p className="text-stone-600 text-base leading-relaxed mt-2">
                            Fale connosco sobre a sua obra. Cada assunto tem a sua conversa e respondemos aqui.
                        </p>
                    </div>
                    {!composing && (
                        <button type="button" onClick={() => setComposing(true)} className={`${BTN} w-full sm:w-auto flex-shrink-0 !py-3.5`} data-testid="client-conversation-new">
                            <Plus className="w-5 h-5" aria-hidden />Nova conversa
                        </button>
                    )}
                </div>
                {composing && <NewConversationForm token={token} onCreated={created} onCancel={() => setComposing(false)} />}
                <ConversationList items={conversations} onOpen={setOpenId} />
            </div>
        </section>
    );
}

function NewConversationForm({ token, onCreated, onCancel }) {
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);
    const ready = title.trim() && body.trim();
    const send = async () => {
        if (!ready || sending) return;
        setSending(true);
        try {
            const { data } = await api.post(`/public/cliente/${token}/action`, { type: "duvida", title: title.trim(), question: body.trim() });
            toast.success(data.message || "Mensagem enviada.");
            await onCreated(data.question_id);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Ocorreu um erro. Tente novamente.");
            setSending(false);
        }
    };
    return (
        <div className="mt-6 space-y-4 rounded-2xl bg-stone-50 border border-stone-200 p-4 sm:p-6" data-testid="client-conversation-form">
            <label className="block">
                <span className={LABEL_TXT}>Assunto</span>
                <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} autoFocus placeholder="Ex.: Escolha do pavimento da cozinha" className={`${FIELD} bg-white`} data-testid="client-conversation-title-input" />
            </label>
            <label className="block">
                <span className={LABEL_TXT}>Mensagem</span>
                <textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} maxLength={2000} placeholder="Escreva aqui a sua mensagem." className={`${FIELD} bg-white resize-none`} data-testid="client-conversation-body-input" />
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
                <button type="button" onClick={send} disabled={!ready || sending} className={BTN} data-testid="client-conversation-submit">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}Enviar
                </button>
                <button type="button" onClick={onCancel} className="px-6 py-4 text-stone-500 text-base hover:text-stone-800 transition-colors" data-testid="client-conversation-cancel">
                    Cancelar
                </button>
            </div>
        </div>
    );
}

function ConversationList({ items, onOpen }) {
    if (!items.length) {
        return <p className="mt-8 text-stone-500 text-base" data-testid="client-conversations-empty">Ainda não tem conversas.</p>;
    }
    return (
        <ul className="mt-8 space-y-3" data-testid="client-conversations-list">
            {items.map((c) => {
                const last = c.messages[c.messages.length - 1];
                return (
                    <li key={c.id}>
                        <button
                            type="button"
                            onClick={() => onOpen(c.id)}
                            className={`group w-full text-left rounded-2xl border p-4 sm:p-5 transition-colors ${c.unread ? "border-orange-200 bg-orange-50/50" : "border-stone-200 hover:border-stone-300 hover:bg-stone-50/60"}`}
                            data-testid={`client-conversation-${c.id}`}
                        >
                            <div className="flex items-start gap-3">
                                <span className="min-w-0 flex-1">
                                    <span className="block font-display text-lg font-bold text-stone-900 leading-snug break-words" data-testid={`client-conversation-title-${c.id}`}>{c.title}</span>
                                    {last && (
                                        <span className="block text-stone-600 text-[15px] mt-1 truncate">
                                            {last.author === "team" ? "O Homem da Obra: " : "Você: "}{last.body}
                                        </span>
                                    )}
                                    <span className="block text-sm text-stone-500 mt-1">{formatWhen(c.last_message_at)}</span>
                                </span>
                                <ChevronRight className="w-5 h-5 mt-1 text-stone-400 flex-shrink-0 transition-transform group-hover:translate-x-0.5" aria-hidden />
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                                {c.unread && <NewPill testid={`client-conversation-unread-${c.id}`} />}
                                <StatusPill status={c.status} testid={`client-conversation-status-${c.id}`} />
                            </div>
                        </button>
                    </li>
                );
            })}
        </ul>
    );
}

function ConversationThread({ token, conv, onBack, onReload }) {
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);
    const resolved = conv.status === "resolved";

    useEffect(() => {
        if (!conv.unread) return;
        api.post(`/public/cliente/${token}/questions/${conv.id}/read`).then(onReload).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conv.id]);

    const send = async () => {
        const text = body.trim();
        if (!text || sending) return;
        setSending(true);
        try {
            await api.post(`/public/cliente/${token}/questions/${conv.id}/messages`, { body: text });
            setBody("");
            toast.success("Mensagem enviada.");
            await onReload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Ocorreu um erro. Tente novamente.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section className={CARD} data-testid="client-conversation-thread">
            <div className="max-w-3xl">
                <button type="button" onClick={onBack} className="inline-flex items-center gap-2 min-h-[44px] text-[15px] font-semibold text-[hsl(var(--copper))] hover:opacity-80 transition-opacity" data-testid="client-conversation-back">
                    <ArrowLeft className="w-4 h-4" aria-hidden />Todas as conversas
                </button>
                <div className="mt-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <h2 className={`${H2} break-words`} data-testid="client-conversation-title">{conv.title}</h2>
                    <span className="self-start flex-shrink-0"><StatusPill status={conv.status} testid="client-conversation-status" /></span>
                </div>
                <ol className="mt-6 space-y-4" data-testid="client-conversation-messages">
                    {conv.messages.map((m) => <MessageBubble key={m.id} m={m} />)}
                </ol>
                <div className="mt-8 pt-6 border-t border-stone-100">
                    {resolved && (
                        <p className="mb-4 flex items-start gap-2.5 text-[15px] text-emerald-900 bg-emerald-50 rounded-2xl p-4" data-testid="client-conversation-resolved-note">
                            <CheckCircle2 className="w-5 h-5 flex-shrink-0 text-emerald-700" aria-hidden />
                            Esta conversa está resolvida. Se precisar, pode responder e ela volta a ficar em aberto.
                        </p>
                    )}
                    <label className="block">
                        <span className={LABEL_TXT}>Responder</span>
                        <textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} maxLength={2000} placeholder="Escreva aqui a sua mensagem." className={`${FIELD} resize-none`} data-testid="client-conversation-reply" />
                    </label>
                    <button type="button" onClick={send} disabled={!body.trim() || sending} className={`${BTN} mt-4 w-full sm:w-auto`} data-testid="client-conversation-send">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}Enviar mensagem
                    </button>
                </div>
            </div>
        </section>
    );
}

function MessageBubble({ m }) {
    const mine = m.author === "client";
    return (
        <li className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`client-message-${m.id}`}>
            <div className={`max-w-[88%] sm:max-w-[75%] rounded-2xl px-4 py-3 ${mine ? "bg-[hsl(var(--forest))] text-white rounded-br-md" : "bg-stone-100 text-stone-900 rounded-bl-md"}`}>
                <div className={`text-xs font-semibold mb-1 ${mine ? "text-white/75" : "text-[hsl(var(--copper))]"}`}>{mine ? "Você" : "O Homem da Obra"}</div>
                <p className="text-base leading-relaxed whitespace-pre-wrap break-words">{m.body}</p>
                <div className={`text-xs mt-1.5 ${mine ? "text-white/60" : "text-stone-500"}`}>{formatWhen(m.created_at)}</div>
            </div>
        </li>
    );
}
